/**
 * App Router Configuration
 *
 * Main router setup for the application.
 * Configures all routes with protection and layout.
 * Each route is wrapped with an ErrorBoundary for isolation.
 *
 * Uses React.lazy() for route-level code splitting - each page
 * loads on-demand for faster initial load times.
 */

import { MainLayout } from "@/components/layout/MainLayout";
import {
  ErrorBoundary,
  ErrorFallback,
} from "@/components/shared/ErrorBoundary";
import { ProtectedRoute } from "@/components/shared/ProtectedRoute";
import { Spinner } from "@/components/ui/feedback/Spinner";
// Fallback pages loaded synchronously (small, needed for error states)
import { ForbiddenPage, NotFoundPage } from "@/pages";
import type { ReactNode } from "react";
import React, { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { DEFAULT_REDIRECT, ROUTES } from "./routes";

// Lazy-loaded page components for code splitting
const BookingPage = React.lazy(() =>
  import("@/pages/booking/BookingPage").then((m) => ({
    default: m.BookingPage,
  })),
);
const OfficePage = React.lazy(() =>
  import("@/pages/office/OfficePage").then((m) => ({ default: m.OfficePage })),
);
const AdminPage = React.lazy(() =>
  import("@/pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const SuperadminPage = React.lazy(() =>
  import("@/pages/superadmin/SuperadminPage").then((m) => ({
    default: m.SuperadminPage,
  })),
);

/**
 * Page loading fallback component
 */
const PageLoadingFallback: React.FC = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
    }}
  >
    <Spinner size="lg" />
  </div>
);

/**
 * Route-level error boundary wrapper
 * Uses location.key as resetKey to reset error state on navigation
 */
const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback
          {...props}
          code="500"
          message="Noe gikk galt på denne siden. Prøv å laste siden på nytt."
          showReset={true}
        />
      )}
      resetKeys={[location.key]}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * App Router Component
 *
 * Provides the main routing configuration for the app.
 * All routes are wrapped with MainLayout for consistent structure.
 * Protected routes are additionally wrapped with ProtectedRoute.
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <MainLayout>
        <Routes>
          {/* Root redirect to booking */}
          <Route
            path={ROUTES.HOME}
            element={<Navigate to={DEFAULT_REDIRECT} replace />}
          />

          {/* Protected routes - each wrapped with RouteErrorBoundary and Suspense for lazy loading */}
          <Route
            path={ROUTES.BOOKING}
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <BookingPage />
                  </Suspense>
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />
          <Route
            path={ROUTES.OFFICE}
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <OfficePage />
                  </Suspense>
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />
          <Route
            path={ROUTES.ADMIN}
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <AdminPage />
                  </Suspense>
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SUPERADMIN}
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SuperadminPage />
                  </Suspense>
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          {/* Public routes */}
          <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
          <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        </Routes>
      </MainLayout>
    </BrowserRouter>
  );
};

export default AppRouter;
